<script lang="ts">
	import { page } from '$app/state';
	import PrimaryButton from '$lib/buttons/primaryButton.svelte';
	import ChevronDown from '$lib/icons/chevronDown.svelte';
	import NavItem from './navItem.svelte';
	import { checkVisibility } from '$lib/actions/checkVisibility';
	import { fade, fly } from 'svelte/transition';
	import Close from '$lib/icons/close.svelte';
	import IconButton from '$lib/buttons/iconButton.svelte';
	import { newsletterFormVisible } from '$lib/stores/footer';
	import { scrollLock } from '$lib/utils/scrollLock.svelte';
	import type { LegalPageItem } from './types';
	import { innerHeight } from 'svelte/reactivity/window';

	interface Props {
		pages: Array<LegalPageItem>;
	}

	let { pages }: Props = $props();

	let activePage = $derived(
		!page.params.slug && pages.length > 0
			? pages.at(-1)!
			: (pages.find((p) => p.page.cached_url.replace(/\/$/, '').endsWith(page.params.slug)) ??
					pages[0])
	);

	let buttonFloating = $state(false);

	let open = $state(false);

	function openMenu() {
		open = true;
		scrollLock.lock();
	}

	function closeMenu() {
		open = false;
		scrollLock.unlock();
	}

	let listHeight = $state(9999);

	const PADDING_SIZE = 136;

	let canScroll = $derived(!innerHeight.current || innerHeight.current < listHeight - PADDING_SIZE);
</script>

<svelte:window
	onkeydown={(e) => {
		if (e.key === 'Escape' && open) {
			open = false;
		}
	}}
/>

<nav class="lg:hidden">
	<div
		use:checkVisibility={{ threshold: 0.75 }}
		onvisible={(e) => {
			if ($newsletterFormVisible) return;

			buttonFloating = !e.detail;
		}}
	>
		<PrimaryButton
			theme="purple"
			wrapperClass="-ml-1 mb-6"
			class="max-w-full text-[16px] leading-[19.2px]"
			onclick={openMenu}
			>{activePage.display_name}
			{#snippet iconTrailing()}<ChevronDown class="h-6 w-6" />{/snippet}</PrimaryButton
		>
	</div>

	{#if buttonFloating}
		<div
			class="fixed inset-x-0 bottom-5 flex items-center justify-start pl-4 pr-25"
			transition:fly|global={{ y: 72, duration: 300 }}
		>
			<PrimaryButton
				theme="purple"
				onclick={openMenu}
				wrapperClass="-ml-1 w-full"
				class="btn-float max-w-full"
				><span class="truncate">{activePage.display_name}</span>
				{#snippet iconTrailing()}<ChevronDown class="h-6 w-6" />{/snippet}</PrimaryButton
			>
		</div>
	{/if}

	{#if open}
		<div
			class="fixed inset-0 z-[100001] flex items-end justify-center bg-stone-5/50 backdrop-blur-md"
			transition:fade={{ duration: 300 }}
		>
			<div
				data-prevent-scroll-ignore
				class={[
					'h-full',
					{
						'overflow-y-scroll': canScroll,
						'flex justify-end': !canScroll
					}
				]}
			>
				<ul
					bind:clientHeight={listHeight}
					class="flex flex-col items-start justify-end gap-y-6 px-container-mobile pb-28 pt-6 text-center text-[18px] font-semibold leading-[19.8px]"
					transition:fly={{ y: 64, duration: 300 }}
				>
					{#each pages as { display_name, page }}
						<NavItem link={page} onclick={closeMenu}>{display_name}</NavItem>
					{/each}
				</ul>
			</div>

			<div
				class={[
					'pointer-events-none absolute bottom-0 flex w-full justify-center pb-6 pt-16',
					{ 'btn-container': canScroll }
				]}
			>
				<div transition:fly={{ y: 64, duration: 300 }} class="pointer-events-auto">
					<IconButton
						wrapperClass="-ml-1"
						colorClass="text-purple-1 border-2 border-purple-1"
						onclick={closeMenu}><Close class="h-6 w-6" /></IconButton
					>
				</div>
			</div>
		</div>
	{/if}
</nav>

<style>
	div.btn-container {
		background: transparent;
		background: linear-gradient(180deg, rgba(248, 244, 235, 0) 0%, rgba(248, 244, 235, 1) 40%);
	}

	:global(button.btn-float) {
		box-shadow: 0px -2px 15px rgba(0, 0, 0, 0.15);
	}
</style>
